import { create } from "zustand";
import type { FormData, ErrorState, NotificationState } from "../types";

export type SignUpMethod = "google" | "email" | null;

export interface AccountCreationState {
  // Form data
  formData: FormData;

  // UI state
  error: ErrorState;
  notification: NotificationState | null;
  loading: boolean;
  googleLoading: boolean;
  showVerification: boolean;
  signUpMethod: SignUpMethod;
  isTaxProConfirmed: boolean;

  // Account creation result (for passing to next steps)
  accountData: any | null;

  // Actions
  setFormData: (formData: Partial<FormData>) => void;
  setFormField: (field: keyof FormData, value: string) => void;
  setError: (error: ErrorState) => void;
  setNotification: (notification: NotificationState | null) => void;
  setLoading: (loading: boolean) => void;
  setGoogleLoading: (loading: boolean) => void;
  setShowVerification: (show: boolean) => void;
  setSignUpMethod: (method: SignUpMethod) => void;
  setIsTaxProConfirmed: (confirmed: boolean) => void;
  setAccountData: (data: any) => void;
  reset: () => void;
}

const initialFormData: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  password: "",
  reenteredPassword: "",
  userBusinessName: "",
};

const initialError: ErrorState = {
  email: "",
  password: "",
};

export const useAccountCreationStore = create<AccountCreationState>()(
  (set) => ({
    // Initial state
    formData: initialFormData,
    error: initialError,
    notification: null,
    loading: false,
    googleLoading: false,
    showVerification: false,
    signUpMethod: null,
    isTaxProConfirmed: false,
    accountData: null,

    // Actions
    setFormData: (formData) =>
      set((state) => ({
        formData: { ...state.formData, ...formData },
      })),

    setFormField: (field, value) =>
      set((state) => ({
        formData: { ...state.formData, [field]: value },
      })),

    setError: (error) => set({ error }),

    setNotification: (notification) => set({ notification }),

    setLoading: (loading) => set({ loading }),

    setGoogleLoading: (googleLoading) => set({ googleLoading }),

    setShowVerification: (showVerification) => {
      console.log(
        "[useAccountCreationStore] setShowVerification called with:",
        showVerification
      );
      set({ showVerification });
    },

    setSignUpMethod: (signUpMethod) => set({ signUpMethod }),

    setIsTaxProConfirmed: (isTaxProConfirmed) => set({ isTaxProConfirmed }),

    setAccountData: (accountData) => set({ accountData }),

    reset: () =>
      set({
        formData: initialFormData,
        error: initialError,
        notification: null,
        loading: false,
        googleLoading: false,
        showVerification: false,
        signUpMethod: null,
        isTaxProConfirmed: false,
        accountData: null,
      }),
  })
);
